import UserService from "../../../../userService";
import type { FarmAndRetreatLink } from "../../../../userManagement.types";

const LOGIN_REGEX = /^[a-zA-Z0-9._-]{3,20}$/;
const EMAIL_REGEX = /^[a-z0-9._-]+@[a-z0-9.-]+\.[a-z]+(\.[a-z]+)?$/;

export enum LoginError {
  InvalidFormat,
  AlreadyExists,
}

export enum EmailError {
  InvalidFormat,
  AlreadyExists,
}

export enum NameError {
  Required,
}

export enum FarmAndRetreatLinksError {
  Required,
}

type ValidationResult<T> =
  | { isValid: true; error: null }
  | { isValid: false; error: T };

type ValidateLoginParams = {
  currentLogin: string | null;
  isEditing: boolean;
  newLogin: string;
};

export async function validateLogin({
  newLogin,
  currentLogin,
  isEditing,
}: ValidateLoginParams): Promise<ValidationResult<LoginError>> {
  if (!LOGIN_REGEX.test(newLogin)) {
    return { isValid: false, error: LoginError.InvalidFormat };
  }

  if (isEditing && currentLogin === newLogin) {
    return { isValid: true, error: null };
  }

  const loginAlreadyExists = await UserService.checkIfLoginAlreadyExists(
    newLogin
  );

  if (loginAlreadyExists) {
    return { isValid: false, error: LoginError.AlreadyExists };
  }

  return { isValid: true, error: null };
}

type ValidateEmailParams = {
  currentEmail: string | null;
  isEditing: boolean;
  newEmail: string;
};

export async function validateEmail({
  currentEmail,
  isEditing,
  newEmail,
}: ValidateEmailParams): Promise<ValidationResult<EmailError>> {
  if (!EMAIL_REGEX.test(newEmail)) {
    return { isValid: false, error: EmailError.InvalidFormat };
  }

  if (isEditing && currentEmail === newEmail) {
    return { isValid: true, error: null };
  }

  const emailAlreadyExists = await UserService.checkIfEmailAlreadyExists(
    newEmail
  );

  if (emailAlreadyExists) {
    return { isValid: false, error: EmailError.AlreadyExists };
  }

  return { isValid: true, error: null };
}

export function validateName(name: string): ValidationResult<NameError> {
  if (name.trim().length === 0) {
    return { isValid: false, error: NameError.Required };
  }

  return { isValid: true, error: null };
}

export function validateFarmAndRetreatLinks(
  links: FarmAndRetreatLink[]
): ValidationResult<FarmAndRetreatLinksError> {
  if (links.length === 0) {
    return { isValid: false, error: FarmAndRetreatLinksError.Required };
  }

  return { isValid: true, error: null };
}
